import { useCallback, useEffect, useRef, useState } from "react";

const defaultPostMessage = (target, message) => {
  target.postMessage(message);
};

const defaultAddMessageListener = (listener) => {
  window.addEventListener("message", listener);

  return () => {
    window.removeEventListener("message", listener);
  };
};

export default function useStrategyBenchmarkChartFrameMessaging({
  chartSeries,
  locale,
  postMessage = defaultPostMessage,
  addMessageListener = defaultAddMessageListener,
  getMessageTarget = frameRef => frameRef.current?.contentWindow
}) {
  const frameRef = useRef(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [chartInitialized, setChartInitialized] = useState(false);

  const sendToFrame = useCallback(
    payload => {
      const target = getMessageTarget(frameRef);

      if (target) {
        postMessage(target, JSON.stringify(payload));
      }
    },
    [getMessageTarget, postMessage]
  );

  const onFrameLoad = useCallback(() => {
    setFrameLoaded(true);
  }, []);

  useEffect(() => {
    const handleMessage = event => {
      const target = getMessageTarget(frameRef);

      if (target && event.source && event.source !== target) {
        return;
      }

      try {
        const data = JSON.parse(event.data);

        if (data.action === "WEBVIEW_LOADED") {
          setFrameLoaded(true);
        }
      } catch (error) {
        // Ignore non-JSON messages from other frames.
      }
    };

    return addMessageListener(handleMessage);
  }, [addMessageListener, getMessageTarget]);

  useEffect(() => {
    if (!frameLoaded || !chartSeries?.length) {
      return;
    }

    if (!chartInitialized) {
      sendToFrame({
        action: "init",
        data: {
          series: chartSeries,
          locale
        }
      });
      setChartInitialized(true);
      return;
    }

    sendToFrame({
      action: "data-update",
      data: {
        series: chartSeries
      }
    });
  }, [chartInitialized, chartSeries, frameLoaded, locale, sendToFrame]);

  return {
    frameRef,
    isFrameReady: frameLoaded && chartInitialized,
    onFrameLoad
  };
}
